/**
 * Dealership 360 · Iter-153 · Service Desk (After-Sales)
 *
 * Three sub-tabs:
 *   1. Reminders   — auto-generated on delivery close; upcoming / due / overdue.
 *   2. Service Quotes — parts (from Inventory) + ad-hoc labour + other charges,
 *      with live GST breakdown and status flow (draft → sent → accepted →
 *      converted / rejected).
 *   3. Job Cards   — reuses the existing JobCards tab for continuity.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { fmtINR, fmtDate } from "@/lib/format";
import { Plus, Trash2, RefreshCw, Wrench, CheckCircle2, ClipboardList, AlertTriangle } from "lucide-react";

import JobCardsTab from "./JobCardsTab";

const STATUS_TONE = {
  upcoming: "text-blue-500", due: "text-amber-600", overdue: "text-red-500",
  done: "text-emerald-600", cancelled: "text-muted-foreground",
  draft: "text-muted-foreground", sent: "text-blue-500", accepted: "text-emerald-600",
  rejected: "text-red-500", converted: "text-purple-600",
};

// ============ REMINDERS ============
function RemindersPanel() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  const load = async () => {
    setBusy(true);
    try {
      const params = {};
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      setRows((await api.get("/dealership/service-reminders", { params })).data || []);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [statusFilter]);

  const refreshStatuses = async () => {
    try {
      const r = await api.post("/dealership/service-reminders/refresh");
      toast.success(`Statuses refreshed · ${r.data.up_to_due + r.data.due_to_over} transitions`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const markDone = async (row) => {
    if (!window.confirm(`Mark reminder "${row.service_name}" for VIN ${row.vin} as done?`)) return;
    try {
      await api.post(`/dealership/service-reminders/${row.id}/mark-done`, {});
      toast.success("Reminder marked done");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const counts = useMemo(() => {
    const c = { upcoming: 0, due: 0, overdue: 0, done: 0 };
    rows.forEach((r) => { if (c[r.status] !== undefined) c[r.status]++; });
    return c;
  }, [rows]);

  return (
    <div className="space-y-3" data-testid="reminders-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex-1">
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">Service Reminders</div>
          <div className="text-[11px] text-muted-foreground">
            Auto-generated when a delivery closes — driven by <b>Config → service_schedule</b>. Free services default to
            upcoming and slide into due / overdue as their date arrives.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-8 rounded-sm w-[130px]" data-testid="reminder-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="upcoming">Upcoming</SelectItem>
              <SelectItem value="due">Due</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
              <SelectItem value="done">Done</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="ghost" onClick={refreshStatuses} className="rounded-sm h-8 gap-1" data-testid="reminders-refresh-statuses">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh statuses
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2">
        {[
          { key: "upcoming", label: "Upcoming", tone: "text-blue-500" },
          { key: "due",      label: "Due",      tone: "text-amber-600" },
          { key: "overdue",  label: "Overdue",  tone: "text-red-500" },
          { key: "done",     label: "Done",     tone: "text-emerald-600" },
        ].map((k) => (
          <div key={k.key} className="card-sharp p-3" data-testid={`kpi-reminder-${k.key}`}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{k.label}</div>
            <div className={`font-heading text-xl mt-1 tabular-nums ${k.tone}`}>{counts[k.key] || 0}</div>
          </div>
        ))}
      </div>

      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr>
            <th>#</th><th>Service</th><th>Type</th><th>VIN</th>
            <th>Booking</th><th>Due date</th><th>Due km</th><th>Status</th><th></th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="9" className="text-center py-10 text-muted-foreground">No reminders in this filter.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`reminder-row-${r.id.slice(0,8)}`}>
                <td className="text-[10px] font-mono text-muted-foreground">#{r.sequence_no}</td>
                <td className="text-xs font-medium">{r.service_name}</td>
                <td className="text-[10px] font-mono uppercase text-muted-foreground">{r.service_type}</td>
                <td className="font-mono text-[10px]">{r.vin || "—"}</td>
                <td className="font-mono text-[10px]">{r.booking_number || "—"}</td>
                <td className="text-xs">{r.due_by_date || "—"}</td>
                <td className="text-xs tabular-nums">{r.due_by_km ? `${r.due_by_km.toLocaleString("en-IN")} km` : "—"}</td>
                <td><span className={`text-[10px] font-mono uppercase ${STATUS_TONE[r.status] || ""}`}>{r.status}</span></td>
                <td className="text-right">
                  {r.status !== "done" && r.status !== "cancelled" && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-emerald-700"
                      onClick={() => markDone(r)} data-testid={`reminder-done-${r.id.slice(0,8)}`}>
                      <CheckCircle2 className="w-3 h-3" /> Done
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============ SERVICE QUOTES ============
function ServiceQuotesPanel() {
  const [rows, setRows] = useState([]);
  const [items, setItems] = useState([]);       // Inventory parts pool
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(null);       // draft state
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [q, it] = await Promise.all([
        api.get("/dealership/service-quotes"),
        api.get("/items", { params: { item_type: "part,spare,consumable,accessory" } }).catch(() => ({ data: [] })),
      ]);
      setRows(q.data || []);
      // Filter items server-side may not honour multi types — filter defensively too.
      const partish = new Set(["part", "spare", "consumable", "accessory"]);
      setItems((it.data || []).filter((x) => partish.has(x.item_type || "part")));
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm({
      vin: "", vehicle_model_id: "",
      customer: { first_name: "", last_name: "", mobile: "" },
      complaints: [""],
      parts: [], labour: [], other: [],
      expiry_days: 15,
    });
    setCreating(true);
  };

  const save = async () => {
    if (!form.vin.trim()) return toast.error("VIN is required");
    setBusy(true);
    try {
      const payload = {
        ...form,
        complaints: (form.complaints || []).filter(Boolean),
      };
      await api.post("/dealership/service-quotes", payload);
      toast.success("Service quote created");
      setCreating(false); setForm(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const setStatus = async (row, status) => {
    try {
      await api.patch(`/dealership/service-quotes/${row.id}/status`, { status });
      toast.success(`Status → ${status}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Iter-154 · Convert accepted quote → Job Card (server creates JC + links it back)
  const convertToJC = async (row) => {
    if (!window.confirm(`Convert quote ${row.quote_number} into a Job Card? Parts, labour and complaints will be copied over.`)) return;
    try {
      const r = await api.post(`/dealership/service-quotes/${row.id}/convert-to-job-card`);
      toast.success(`Job Card ${r.data.job_card?.job_card_number || ""} created — see Job Cards tab`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // ─── Live totals for the create sheet ───
  const totals = useMemo(() => {
    if (!form) return null;
    const parts = form.parts.reduce((acc, p) => {
      const sub = (Number(p.qty) || 0) * (Number(p.unit_price) || 0);
      const gst = sub * (Number(p.gst_rate) || 0) / 100;
      acc.parts_sub += sub; acc.parts_gst += gst;
      return acc;
    }, { parts_sub: 0, parts_gst: 0 });
    const lab = form.labour.reduce((acc, l) => {
      const sub = (Number(l.hours) || 0) * (Number(l.rate) || 0);
      const gst = sub * (Number(l.gst_rate) || 0) / 100;
      acc.lab_sub += sub; acc.lab_gst += gst;
      return acc;
    }, { lab_sub: 0, lab_gst: 0 });
    const oth = form.other.reduce((acc, o) => {
      const sub = Number(o.amount) || 0;
      const gst = sub * (Number(o.gst_rate) || 0) / 100;
      acc.oth_sub += sub; acc.oth_gst += gst;
      return acc;
    }, { oth_sub: 0, oth_gst: 0 });
    const subtotal = parts.parts_sub + lab.lab_sub + oth.oth_sub;
    const gst_total = parts.parts_gst + lab.lab_gst + oth.oth_gst;
    return {
      ...parts, ...lab, ...oth,
      subtotal, gst_total, grand_total: subtotal + gst_total,
    };
  }, [form]);

  return (
    <div className="space-y-3" data-testid="service-quotes-panel">
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">Paid Service Quotes</div>
          <div className="text-[11px] text-muted-foreground">
            Parts pulled from Inventory · ad-hoc labour rows · other charges. Live GST breakdown.
          </div>
        </div>
        <Button size="sm" onClick={openCreate} className="h-8 rounded-sm bg-accent hover:bg-accent/90 text-white gap-1" data-testid="service-quote-new">
          <Plus className="w-3 h-3" /> New quote
        </Button>
      </div>

      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr>
            <th>Quote #</th><th>VIN</th><th>Customer</th>
            <th className="text-right">Subtotal</th><th className="text-right">GST</th><th className="text-right">Total</th>
            <th>Status</th><th>Expires</th><th></th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="9" className="text-center py-10 text-muted-foreground">No quotes yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`sq-row-${r.quote_number}`}>
                <td className="font-mono text-xs">{r.quote_number}</td>
                <td className="font-mono text-[10px]">{r.vin || "—"}</td>
                <td className="text-xs">{r.customer?.first_name} {r.customer?.last_name}</td>
                <td className="text-right tabular-nums">{fmtINR(r.subtotal)}</td>
                <td className="text-right tabular-nums text-muted-foreground">{fmtINR(r.gst_total)}</td>
                <td className="text-right tabular-nums font-medium">{fmtINR(r.grand_total)}</td>
                <td><span className={`text-[10px] font-mono uppercase ${STATUS_TONE[r.status] || ""}`}>{r.status}</span></td>
                <td className="text-[10px] font-mono text-muted-foreground">{r.expiry_date || "—"}</td>
                <td className="text-right whitespace-nowrap">
                  {r.status === "draft" && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-blue-600"
                      onClick={() => setStatus(r, "sent")} data-testid={`sq-send-${r.quote_number}`}>Send</Button>
                  )}
                  {r.status === "sent" && (
                    <>
                      <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-emerald-700"
                        onClick={() => setStatus(r, "accepted")} data-testid={`sq-accept-${r.quote_number}`}>Accept</Button>
                      <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-red-600"
                        onClick={() => setStatus(r, "rejected")} data-testid={`sq-reject-${r.quote_number}`}>Reject</Button>
                    </>
                  )}
                  {r.status === "accepted" && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-purple-700"
                      onClick={() => convertToJC(r)} data-testid={`sq-convert-${r.quote_number}`}>Convert → Job Card</Button>
                  )}
                  {r.status === "converted" && r.job_card_number && (
                    <span className="text-[10px] font-mono uppercase text-emerald-600" title="Linked job card">
                      ✓ {r.job_card_number}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create sheet */}
      <Sheet open={creating} onOpenChange={(v) => { if (!v) { setCreating(false); setForm(null); } }}>
        <SheetContent side="right" className="w-full sm:max-w-3xl rounded-none space-y-3 overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-heading flex items-center gap-1"><ClipboardList className="w-4 h-4" /> New Service Quote</SheetTitle>
            <SheetDescription>Draft a paid-service quotation with parts, labour, and other charges. GST is auto-computed per line.</SheetDescription>
          </SheetHeader>
          {form && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-2">
                <div><Label className="text-xs">VIN *</Label>
                  <Input value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value.toUpperCase() })}
                    className="rounded-sm mt-1 font-mono" data-testid="sq-vin" /></div>
                <div><Label className="text-xs">Customer first name</Label>
                  <Input value={form.customer.first_name} onChange={(e) => setForm({ ...form, customer: { ...form.customer, first_name: e.target.value } })}
                    className="rounded-sm mt-1" data-testid="sq-customer-fn" /></div>
                <div><Label className="text-xs">Mobile</Label>
                  <Input value={form.customer.mobile} onChange={(e) => setForm({ ...form, customer: { ...form.customer, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) } })}
                    className="rounded-sm mt-1 font-mono" data-testid="sq-customer-mobile" /></div>
              </div>

              <div>
                <Label className="text-xs">Complaints (one per row)</Label>
                {form.complaints.map((c, i) => (
                  <div key={i} className="flex gap-2 mt-1">
                    <Input value={c} onChange={(e) => setForm({ ...form, complaints: form.complaints.map((x, j) => j === i ? e.target.value : x) })}
                      placeholder="Customer complaint…" className="rounded-sm" data-testid={`sq-complaint-${i}`} />
                    <button className="text-muted-foreground hover:text-red-500"
                      onClick={() => setForm({ ...form, complaints: form.complaints.filter((_, j) => j !== i) })}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
                <Button size="sm" variant="ghost" onClick={() => setForm({ ...form, complaints: [...form.complaints, ""] })} className="rounded-sm h-7 text-[10px] mt-1"><Plus className="w-3 h-3 mr-1" /> Add complaint</Button>
              </div>

              {/* PARTS */}
              <div className="border-t pt-3">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-blue-600">Parts (from Inventory)</div>
                  <Button size="sm" variant="ghost" onClick={() => setForm({ ...form, parts: [...form.parts, { item_id: "", qty: 1, unit_price: 0, gst_rate: 18, name: "" }] })}
                    className="rounded-sm h-6 text-[10px]" data-testid="sq-add-part"><Plus className="w-3 h-3 mr-1" /> Add part</Button>
                </div>
                {form.parts.map((p, i) => (
                  <div key={i} className="grid grid-cols-12 gap-1 mb-1 items-center text-xs" data-testid={`sq-part-row-${i}`}>
                    <Select value={p.item_id || ""}
                      onValueChange={(v) => {
                        const it = items.find((x) => x.id === v);
                        setForm({ ...form, parts: form.parts.map((x, j) => j === i ? {
                          ...x, item_id: v,
                          name: it?.name || x.name,
                          unit_price: it?.sale_price ?? x.unit_price,
                          gst_rate: it?.gst_rate ?? x.gst_rate,
                        } : x) });
                      }}>
                      <SelectTrigger className="col-span-5 h-8 rounded-sm text-xs"><SelectValue placeholder="Pick a part…" /></SelectTrigger>
                      <SelectContent className="max-h-64">
                        {items.length === 0 && <div className="px-3 py-2 text-[10px] text-muted-foreground">No parts in Inventory yet.</div>}
                        {items.map((it) => <SelectItem key={it.id} value={it.id}>{it.sku || ""} · {it.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input type="number" value={p.qty} onChange={(e) => setForm({ ...form, parts: form.parts.map((x, j) => j === i ? { ...x, qty: Number(e.target.value) } : x) })}
                      className="col-span-1 h-8 rounded-sm font-mono" placeholder="qty" />
                    <Input type="number" value={p.unit_price} onChange={(e) => setForm({ ...form, parts: form.parts.map((x, j) => j === i ? { ...x, unit_price: Number(e.target.value) } : x) })}
                      className="col-span-2 h-8 rounded-sm font-mono" placeholder="rate" />
                    <Input type="number" value={p.gst_rate} onChange={(e) => setForm({ ...form, parts: form.parts.map((x, j) => j === i ? { ...x, gst_rate: Number(e.target.value) } : x) })}
                      className="col-span-1 h-8 rounded-sm font-mono" placeholder="GST%" />
                    <div className="col-span-2 text-right text-[10px] font-mono">{fmtINR((p.qty || 0) * (p.unit_price || 0))}</div>
                    <button className="col-span-1 text-muted-foreground hover:text-red-500"
                      onClick={() => setForm({ ...form, parts: form.parts.filter((_, j) => j !== i) })}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>

              {/* LABOUR */}
              <div className="border-t pt-3">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-purple-600">Labour (ad-hoc rows)</div>
                  <Button size="sm" variant="ghost" onClick={() => setForm({ ...form, labour: [...form.labour, { description: "", hours: 1, rate: 500, gst_rate: 18 }] })}
                    className="rounded-sm h-6 text-[10px]" data-testid="sq-add-labour"><Plus className="w-3 h-3 mr-1" /> Add labour</Button>
                </div>
                {form.labour.map((l, i) => (
                  <div key={i} className="grid grid-cols-12 gap-1 mb-1 items-center text-xs" data-testid={`sq-labour-row-${i}`}>
                    <Input value={l.description} onChange={(e) => setForm({ ...form, labour: form.labour.map((x, j) => j === i ? { ...x, description: e.target.value } : x) })}
                      placeholder="Labour description…" className="col-span-5 h-8 rounded-sm" />
                    <Input type="number" value={l.hours} onChange={(e) => setForm({ ...form, labour: form.labour.map((x, j) => j === i ? { ...x, hours: Number(e.target.value) } : x) })}
                      className="col-span-1 h-8 rounded-sm font-mono" placeholder="hrs" />
                    <Input type="number" value={l.rate} onChange={(e) => setForm({ ...form, labour: form.labour.map((x, j) => j === i ? { ...x, rate: Number(e.target.value) } : x) })}
                      className="col-span-2 h-8 rounded-sm font-mono" placeholder="₹/hr" />
                    <Input type="number" value={l.gst_rate} onChange={(e) => setForm({ ...form, labour: form.labour.map((x, j) => j === i ? { ...x, gst_rate: Number(e.target.value) } : x) })}
                      className="col-span-1 h-8 rounded-sm font-mono" placeholder="GST%" />
                    <div className="col-span-2 text-right text-[10px] font-mono">{fmtINR((l.hours || 0) * (l.rate || 0))}</div>
                    <button className="col-span-1 text-muted-foreground hover:text-red-500"
                      onClick={() => setForm({ ...form, labour: form.labour.filter((_, j) => j !== i) })}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>

              {/* OTHER */}
              <div className="border-t pt-3">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-amber-600">Other charges</div>
                  <Button size="sm" variant="ghost" onClick={() => setForm({ ...form, other: [...form.other, { description: "", amount: 0, gst_rate: 18 }] })}
                    className="rounded-sm h-6 text-[10px]" data-testid="sq-add-other"><Plus className="w-3 h-3 mr-1" /> Add other</Button>
                </div>
                {form.other.map((o, i) => (
                  <div key={i} className="grid grid-cols-12 gap-1 mb-1 items-center text-xs" data-testid={`sq-other-row-${i}`}>
                    <Input value={o.description} onChange={(e) => setForm({ ...form, other: form.other.map((x, j) => j === i ? { ...x, description: e.target.value } : x) })}
                      placeholder="Other charge…" className="col-span-6 h-8 rounded-sm" />
                    <Input type="number" value={o.amount} onChange={(e) => setForm({ ...form, other: form.other.map((x, j) => j === i ? { ...x, amount: Number(e.target.value) } : x) })}
                      className="col-span-2 h-8 rounded-sm font-mono" placeholder="amount" />
                    <Input type="number" value={o.gst_rate} onChange={(e) => setForm({ ...form, other: form.other.map((x, j) => j === i ? { ...x, gst_rate: Number(e.target.value) } : x) })}
                      className="col-span-1 h-8 rounded-sm font-mono" placeholder="GST%" />
                    <div className="col-span-2 text-right text-[10px] font-mono">{fmtINR(o.amount || 0)}</div>
                    <button className="col-span-1 text-muted-foreground hover:text-red-500"
                      onClick={() => setForm({ ...form, other: form.other.filter((_, j) => j !== i) })}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>

              {/* LIVE TOTALS */}
              {totals && (
                <div className="border-t pt-3 bg-slate-50/60 -mx-6 px-6 py-3 sticky bottom-0" data-testid="sq-live-totals">
                  <div className="grid grid-cols-4 gap-3 text-[10px]">
                    <div>
                      <div className="font-mono uppercase text-muted-foreground">Parts</div>
                      <div className="tabular-nums font-medium">{fmtINR(totals.parts_sub)} + {fmtINR(totals.parts_gst)}</div>
                    </div>
                    <div>
                      <div className="font-mono uppercase text-muted-foreground">Labour</div>
                      <div className="tabular-nums font-medium">{fmtINR(totals.lab_sub)} + {fmtINR(totals.lab_gst)}</div>
                    </div>
                    <div>
                      <div className="font-mono uppercase text-muted-foreground">Other</div>
                      <div className="tabular-nums font-medium">{fmtINR(totals.oth_sub)} + {fmtINR(totals.oth_gst)}</div>
                    </div>
                    <div>
                      <div className="font-mono uppercase text-muted-foreground">Grand Total</div>
                      <div className="tabular-nums font-heading text-lg text-accent">{fmtINR(totals.grand_total)}</div>
                    </div>
                  </div>
                  <Button onClick={save} disabled={busy}
                    className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white mt-2" data-testid="sq-save">
                    {busy ? "Saving…" : "Save Draft Quote"}
                  </Button>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ============ MAIN ============
export default function ServiceDeskTab() {
  return (
    <div className="space-y-3" data-testid="service-desk-tab">
      <Tabs defaultValue="reminders">
        <TabsList className="rounded-sm">
          <TabsTrigger value="reminders" className="rounded-sm" data-testid="tab-sd-reminders">Reminders</TabsTrigger>
          <TabsTrigger value="quotes" className="rounded-sm" data-testid="tab-sd-quotes">Service Quotes</TabsTrigger>
          <TabsTrigger value="jobcards" className="rounded-sm" data-testid="tab-sd-jobcards">Job Cards</TabsTrigger>
        </TabsList>
        <TabsContent value="reminders" className="mt-3"><RemindersPanel /></TabsContent>
        <TabsContent value="quotes" className="mt-3"><ServiceQuotesPanel /></TabsContent>
        <TabsContent value="jobcards" className="mt-3"><JobCardsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
